import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Check } from "lucide-react";

export const LandingPricing = () => {
  const plans = [
    {
      name: "Mensal",
      price: "R$ 16,00",
      period: "/mês",
      description: (
        <>
          Flexível e leve.
          <br />
          Cancele a qualquer momento.
        </>
      ),
      cta: "Testar grátis agora",
    },
    {
      name: "Anual",
      price: "R$ 12,50",
      period: "/mês",
      description: (
        <>
          Autoconhecimento com 22% de desconto.
          <br />
          Cobrança anual de R$ 150,00.
        </>
      ),
      cta: "Testar grátis agora",
    },
  ];

  return (
    <section className="relative w-full min-h-[659px] py-16 md:py-24 px-6 md:px-12 overflow-hidden">
      {/* Aqueous Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#FFD166]/8 to-white -z-10"></div>

      {/* Animated Blobs */}
      <div
        className="absolute top-0 left-[15%] w-[500px] h-[500px] rounded-full opacity-10 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(74, 78, 105, 0.4) 0%, rgba(74, 78, 105, 0) 70%)",
          animation: "float 25s ease-in-out infinite",
        }}
      ></div>
      <div
        className="absolute bottom-0 right-[10%] w-[600px] h-[600px] rounded-full opacity-12 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(255, 209, 102, 0.5) 0%, rgba(255, 209, 102, 0) 70%)",
          animation: "float 30s ease-in-out infinite reverse",
        }}
      ></div>

      <div
        className="text-center mb-12"
        style={{
          animation: "fadeInUp 0.8s ease-out",
        }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-[#4A4E69] mb-4">Escolha o ritmo ideal para sua jornada</h2>
        <p className="text-base md:text-lg text-[#272D37]/70 max-w-2xl mx-auto">
          Acesso total à KopfAI.
          <br />
          Você só escolhe a frequência do pagamento.
        </p>
      </div>

      <div className="flex flex-col md:flex-row justify-center items-center md:items-stretch gap-8 max-w-5xl mx-auto">
        {plans.map((plan, index) => (
          <div key={index} className="contents">
            <div
              className="group relative flex flex-col items-center text-center w-full md:w-auto md:flex-1 max-w-[400px]"
              style={{
                animation: `fadeInUp 0.8s ease-out ${0.2 + index * 0.2}s backwards`,
              }}
            >
              {/* Top accent badge - Outside card */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                <div className="px-5 py-2 rounded-full bg-gradient-to-r from-[#FFD166] to-[#FFD166]/80 shadow-lg border border-[#FFD166]/30">
                  <span className="text-sm font-bold text-[#4A4E69] whitespace-nowrap">{plan.name}</span>
                </div>
              </div>

              {/* Card with glassmorphism */}
              <div className="relative w-full bg-white/60 backdrop-blur-xl rounded-3xl border border-[#D8DEE9]/50 p-8 md:p-10 pt-12 shadow-xl transition-all duration-500 hover:shadow-2xl hover:-translate-y-3 hover:border-[#FFD166]/50 overflow-hidden">
                {/* Glow effect on hover */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-2xl"
                  style={{
                    background: "radial-gradient(circle at 50% 0%, rgba(255, 209, 102, 0.3), transparent 70%)",
                  }}
                ></div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Price */}
                  <div className="mb-6 mt-2">
                    <span className="text-4xl md:text-5xl font-extrabold text-[#4A4E69] transition-all duration-300 group-hover:scale-110 inline-block">
                      {plan.price}
                    </span>
                    <span className="text-xl md:text-2xl text-[#272D37]/70 font-medium">{plan.period}</span>
                  </div>

                  {/* Description */}
                  <div className="min-h-[80px] mb-6">
                    <p className="text-base text-[#272D37]/75 leading-relaxed">{plan.description}</p>
                  </div>

                  {/* CTA Button */}
                  <Link to="/auth?tab=signup" className="w-full">
                    <Button className="w-full h-[53px] rounded-full text-lg font-bold bg-transparent border-2 border-[#4A4E69] text-[#4A4E69] hover:bg-[#4A4E69] hover:text-white transition-all duration-300 hover:shadow-xl hover:scale-105">
                      {plan.cta}
                    </Button>
                  </Link>
                </div>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FFD166] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-3xl"></div>
              </div>
            </div>

            {/* Divider */}
            {index === 0 && (
              <div
                className="hidden md:flex items-center justify-center self-center"
                style={{
                  animation: "fadeIn 0.8s ease-out 0.6s backwards",
                }}
              >
                <div className="relative w-1 h-32 rounded-full overflow-hidden">
                  {/* Gradient divider */}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FFD166] to-transparent"></div>

                  {/* Animated glow */}
                  <div
                    className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FFD166] to-transparent opacity-50 blur-md"
                    style={{
                      animation: "pulse 3s ease-in-out infinite",
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
          }
          50% { 
            transform: translate(40px, 40px) scale(1.12); 
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </section>
  );
};
